import { useState, useEffect, useCallback } from 'react'
import { MainScreen } from './components/MainScreen'
import { AddressSearchScreen } from './components/AddressSearchScreen'
import { OrderScreen } from './components/OrderScreen'
import { OrderHistory } from './components/OrderHistory'
import { TariffDetails } from './components/TariffDetails'
import { getRouteStats, createOrder } from './services/api'
import type { AddressResult, ServiceLevel } from './types'

export type Screen = 'main' | 'search-from' | 'search-to' | 'search-intermediate' | 'order' | 'tariff-details' | 'history'

// User ID для заказов (зарегистрированный в Yandex B2B)
const YANDEX_USER_ID = 'ee0cf66d969d4c37951aae233a8df220'

function App() {
  const [screen, setScreen] = useState<Screen>('main')
  const [fromAddress, setFromAddress] = useState<AddressResult | null>(null)
  const [toAddress, setToAddress] = useState<AddressResult | null>(null)
  const [intermediateAddresses, setIntermediateAddresses] = useState<AddressResult[]>([])
  const [selectedTariff, setSelectedTariff] = useState<string>('')
  const [isOrdering, setIsOrdering] = useState(false)
  
  // Реальные тарифы из API
  const [tariffs, setTariffs] = useState<ServiceLevel[]>([])
  const [offer, setOffer] = useState<string | null>(null)
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false)
  const [tariffsError, setTariffsError] = useState<string | null>(null)
  
  // ID созданного заказа
  const [orderId, setOrderId] = useState<string | null>(null)
  
  // Данные подъезда
  const [entrance, setEntrance] = useState('')

  // Загрузка тарифов при выборе обоих адресов
  const fetchTariffs = useCallback(async () => {
    if (!fromAddress || !toAddress) {
      setTariffs([])
      setOffer(null)
      return
    }

    setIsLoadingTariffs(true)
    setTariffsError(null)

    try {
      // Формируем маршрут: [lon, lat]
      const route: [number, number][] = [
        [fromAddress.lon, fromAddress.lat],
        ...intermediateAddresses.map(addr => [addr.lon, addr.lat] as [number, number]),
        [toAddress.lon, toAddress.lat],
      ]

      const response = await getRouteStats(route, YANDEX_USER_ID)
      
      if (response.service_levels && response.service_levels.length > 0) {
        setTariffs(response.service_levels)
        setOffer(response.offer || null)
        // Выбираем первый доступный тариф по умолчанию только если текущий не установлен
        setSelectedTariff(prev => {
          if (!prev || !response.service_levels.find(t => t.class === prev)) {
            return response.service_levels[0].class
          }
          return prev
        })
      } else {
        setTariffsError('Нет доступных тарифов для этого маршрута')
      }
    } catch (error: any) {
      console.error('Failed to fetch tariffs:', error)
      const errorMessage = error?.message || 'Ошибка загрузки тарифов. Попробуйте снова.'
      setTariffsError(errorMessage)
    } finally {
      setIsLoadingTariffs(false)
    }
  }, [fromAddress, toAddress, intermediateAddresses]) // Removed selectedTariff from dependencies

  // Загружаем тарифы при изменении адресов
  useEffect(() => {
    fetchTariffs()
  }, [fetchTariffs])

  // Handle address selection
  const handleAddressSelect = (address: AddressResult, type: 'from' | 'to' | 'intermediate') => {
    if (type === 'from') {
      setFromAddress(address)
    } else if (type === 'to') {
      setToAddress(address)
    } else if (type === 'intermediate') {
      setIntermediateAddresses([...intermediateAddresses, address])
    }
    setScreen('main')
  }

  // Remove intermediate address
  const handleRemoveIntermediate = (index: number) => {
    setIntermediateAddresses(intermediateAddresses.filter((_, i) => i !== index))
  }

  // Handle order - реальное создание заказа
  const handleOrder = async () => {
    if (!fromAddress || !toAddress || !selectedTariff || isOrdering) return
    
    // Prevent any state updates during order creation
    setIsOrdering(true)

    try {
      // Формируем маршрут для заказа
      const route = [
        {
          geopoint: [fromAddress.lon, fromAddress.lat] as [number, number],
          fullname: fromAddress.display_name,
        },
        ...intermediateAddresses.map(addr => ({
          geopoint: [addr.lon, addr.lat] as [number, number],
          fullname: addr.display_name,
        })),
        {
          geopoint: [toAddress.lon, toAddress.lat] as [number, number],
          fullname: toAddress.display_name,
        },
      ]

      const response = await createOrder({
        user_id: YANDEX_USER_ID,
        route,
        class: selectedTariff,
        offer: offer || undefined,
      })

      // Set order ID and navigate to order screen in a batch
      setOrderId(response.order_id)
      // Small delay to ensure state is set before screen transition
      requestAnimationFrame(() => {
        setScreen('order')
        setIsOrdering(false)
      })
    } catch (error: any) {
      console.error('Failed to create order:', error)
      const errorMessage = error?.response?.data?.detail || error?.message || 'Ошибка создания заказа. Попробуйте снова.'
      alert(errorMessage)
      setIsOrdering(false)
    }
  }

  // Reset order
  const handleNewOrder = () => {
    setFromAddress(null)
    setToAddress(null)
    setIntermediateAddresses([])
    setSelectedTariff('')
    setEntrance('')
    setTariffs([])
    setOffer(null)
    setOrderId(null)
    setScreen('main')
  }

  // Open tariff details
  const handleTariffDetails = (tariff: string) => {
    setSelectedTariff(tariff)
    setScreen('tariff-details')
  }

  // Get selected tariff data
  const selectedTariffData = tariffs.find(t => t.class === selectedTariff)

  return (
    <div className="h-screen w-screen bg-dark overflow-hidden">
      {screen === 'main' && (
        <MainScreen
          fromAddress={fromAddress}
          toAddress={toAddress}
          intermediateAddresses={intermediateAddresses}
          tariffs={tariffs}
          selectedTariff={selectedTariff}
          isOrdering={isOrdering}
          isLoadingTariffs={isLoadingTariffs}
          tariffsError={tariffsError}
          onFromClick={() => setScreen('search-from')}
          onToClick={() => setScreen('search-to')}
          onAddIntermediateClick={() => setScreen('search-intermediate')}
          onRemoveIntermediate={handleRemoveIntermediate}
          onTariffSelect={setSelectedTariff}
          onOrder={handleOrder}
          onTariffDetails={handleTariffDetails}
          entrance={entrance}
          onEntranceChange={setEntrance}
          onHistoryClick={() => setScreen('history')}
        />
      )}

      {screen === 'search-from' && (
        <AddressSearchScreen
          type="from"
          onSelect={(addr) => handleAddressSelect(addr, 'from')}
          onBack={() => setScreen('main')}
        />
      )}

      {screen === 'search-to' && (
        <AddressSearchScreen
          type="to"
          onSelect={(addr) => handleAddressSelect(addr, 'to')}
          onBack={() => setScreen('main')}
        />
      )}

      {screen === 'search-intermediate' && (
        <AddressSearchScreen
          type="to"
          onSelect={(addr) => handleAddressSelect(addr, 'intermediate')}
          onBack={() => setScreen('main')}
        />
      )}

      {screen === 'order' && orderId && (
        <OrderScreen
          orderId={orderId}
          fromAddress={fromAddress}
          toAddress={toAddress}
          tariff={selectedTariff}
          price={selectedTariffData?.price_rub || 0}
          priceUsdt={selectedTariffData?.price_usdt || 0}
          onNewOrder={handleNewOrder}
        />
      )}

      {screen === 'history' && (
        <OrderHistory
          userId={YANDEX_USER_ID}
          onBack={() => setScreen('main')}
          onOpenOrder={(yandexOrderId) => {
            setOrderId(yandexOrderId)
            setScreen('order')
          }}
        />
      )}

      {screen === 'tariff-details' && selectedTariffData && (
        <TariffDetails
          tariff={selectedTariffData}
          fromAddress={fromAddress?.display_name.split(',')[0]}
          toAddress={toAddress?.display_name.split(',')[0]}
          onClose={() => setScreen('main')}
          onOrder={handleOrder}
        />
      )}
    </div>
  )
}

export default App
